import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Layout,
  Input,
  List,
  Card,
  Row,
  Col,
  Table,
  Typography,
  Button,
  Space,
  message,
  Avatar,
  Popconfirm,
  Upload,
  Modal,
  Form,
  Tag,
  Select,
  InputNumber,
  Progress,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  DownloadOutlined,
  DeleteOutlined,
  UploadOutlined,
  FileTextOutlined,
  FolderOutlined,
  EditOutlined,
  AppstoreOutlined,
  BarsOutlined,
} from '@ant-design/icons';
import { Radio } from 'antd';
import api from '../services/api';
import { Customer } from '../services/customers';
import { Business, businessService } from '../services/businesses';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import dayjs from 'dayjs';

const { Sider, Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface CustomerReport {
  id: number;
  customer_id: number;
  business_id?: number | null;
  name: string;
  path: string;
  type: string;
  mime_type: string;
  size: number;
  folder: string | null;
  report_type: 'SSM' | 'Financial' | null;
  task: string | null;
  year: number | null;
  description: string | null;
  created_at: string;
  updated_at: string;
  customer?: Customer;
  formatted_size?: string;
}

interface Folder {
  folder: string;
  customer_id: number;
  business_id?: number | null;
  customer_name: string;
  file_count?: number;
  year?: number;
  report_type?: 'SSM' | 'Financial' | null;
}

export const Reports: React.FC = () => {
  const [entityType, setEntityType] = useState<'customer' | 'business'>('customer');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
  const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);
  const [folderView, setFolderView] = useState<'grid' | 'list'>('grid');
  const [reportView, setReportView] = useState<'grid' | 'list'>('grid');
  const [reports, setReports] = useState<CustomerReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [businessesLoading, setBusinessesLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [folderSearchText, setFolderSearchText] = useState('');
  const [reportSearchText, setReportSearchText] = useState('');
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [createFolderModalVisible, setCreateFolderModalVisible] = useState(false);
  const [editFolderModalVisible, setEditFolderModalVisible] = useState(false);
  const [editingFolder, setEditingFolder] = useState<Folder | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadForm] = Form.useForm();
  const [createFolderForm] = Form.useForm();
  const [editFolderForm] = Form.useForm();
  const [isDragging, setIsDragging] = useState(false);
  const [uploadQueue, setUploadQueue] = useState<Array<{ file: File; id: string }>>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [uploadStatus, setUploadStatus] = useState<Record<string, 'pending' | 'uploading' | 'success' | 'error'>>({});
  const uploadQueueRef = useRef<Array<{ file: File; id: string }>>([]);
  const uploadStatusRef = useRef<Record<string, 'pending' | 'uploading' | 'success' | 'error'>>({});
  const isProcessingRef = useRef(false);
  const { user } = useAuth();
  const canView = hasPermission(user, 'reports.view');
  const canCreate = hasPermission(user, 'reports.create');
  const canUpdate = hasPermission(user, 'reports.update');
  const CONCURRENT_UPLOADS = 5;
  
  const location = useLocation();
  // Check if user is customer (not admin)
  // If path starts with /customer, user is customer, otherwise admin
  const isCustomer = location.pathname.startsWith('/customer');

  useEffect(() => {
    if (!canView) {
      setCustomers([]);
      setBusinesses([]);
      setFolders([]);
      setReports([]);
      return;
    }
    if (entityType === 'customer') {
      loadCustomers();
    } else {
      loadBusinesses();
    }
  }, [entityType, canView]);

  useEffect(() => {
    // Reset selection when switching entity type
    setSelectedCustomer(null);
    setSelectedBusiness(null);
    setSelectedFolder(null);
    setFolders([]);
    setReports([]);
    setFolderSearchText('');
    setReportSearchText('');
  }, [entityType]);

  useEffect(() => {
    if (entityType === 'customer' && selectedCustomer) {
      loadFolders(selectedCustomer.id, null);
      setSelectedFolder(null);
      setReports([]);
    } else if (entityType === 'business' && selectedBusiness) {
      (async () => {
        const customerId = await resolveBusinessCustomerId();
        if (customerId) {
          loadFolders(customerId, selectedBusiness.id);
          setSelectedFolder(null);
          setReports([]);
        }
      })();
    } else {
      setFolders([]);
      setSelectedFolder(null);
      setReports([]);
    }
  }, [selectedCustomer, selectedBusiness, entityType]);

  useEffect(() => {
    if (selectedFolder) {
      // Use business_id from selectedFolder if available, otherwise use selectedBusiness
      const businessId = selectedFolder.business_id ?? (entityType === 'business' && selectedBusiness ? selectedBusiness.id : null);
      loadReports(selectedFolder.customer_id, selectedFolder.folder, businessId);
    } else {
      setReports([]);
    }
    setReportSearchText('');
  }, [selectedFolder, entityType, selectedBusiness]);

  const loadCustomers = async () => {
    if (!canView) {
      setCustomers([]);
      return;
    }
    setCustomersLoading(true);
    try {
      const response = await api.get('/reports/customers');
      setCustomers(response.data.data || []);
    } catch (error: any) {
      message.error('Failed to load customers');
    } finally {
      setCustomersLoading(false);
    }
  };

  const loadBusinesses = async () => {
    if (!canView) {
      setBusinesses([]);
      return;
    }
    setBusinessesLoading(true);
    try {
      const response = await api.get('/reports/businesses');
      setBusinesses(response.data.data || []);
    } catch (error: any) {
      message.error('Failed to load businesses');
    } finally {
      setBusinessesLoading(false);
    }
  };

  const resolveBusinessCustomerId = async () => {
    if (!selectedBusiness) return null;
    if (selectedBusiness.customer_id) {
      return selectedBusiness.customer_id;
    }
    try {
      const business = await businessService.getById(selectedBusiness.id);
      setSelectedBusiness(business);
      return business.customer_id;
    } catch {
      message.error('Failed to load business customer');
      return null;
    }
  };

  const loadFolders = async (customerId: number, businessId: number | null = null) => {
    if (!canView) {
      setFolders([]);
      return;
    }
    setFoldersLoading(true);
    try {
      let url = `/reports/folders?customer_id=${customerId}`;
      if (businessId) {
        url += `&business_id=${businessId}`;
      }
      const response = await api.get(url);
      setFolders(response.data.data || []);
    } catch (error: any) {
      message.error('Failed to load folders');
    } finally {
      setFoldersLoading(false);
    }
  };

  const loadReports = async (customerId: number, folder: string, businessId: number | null = null) => {
    if (!canView) {
      setReports([]);
      return;
    }
    setLoading(true);
    try {
      let url = `/reports?customer_id=${customerId}`;
      if (businessId) {
        url += `&business_id=${businessId}`;
      }
      const response = await api.get(url);
      const allReports = response.data.data || [];
      // Filter reports by folder AND business_id to ensure correct folder
      const filteredReports = allReports.filter((report: CustomerReport) => {
        const folderMatch = report.folder === folder;
        const businessMatch = businessId 
          ? (report.business_id === businessId)
          : (report.business_id === null || report.business_id === undefined);
        return folderMatch && businessMatch;
      });
      setReports(filteredReports);
    } catch (error: any) {
      message.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (value: string) => {
    setSearchText(value);
    if (entityType === 'customer') {
      setCustomersLoading(true);
      try {
        const response = await api.get(`/reports/customers?search=${encodeURIComponent(value)}`);
        setCustomers(response.data.data || []);
      } catch (error: any) {
        message.error('Failed to search customers');
      } finally {
        setCustomersLoading(false);
      }
    } else {
      setBusinessesLoading(true);
      try {
        const response = await api.get(`/reports/businesses?search=${encodeURIComponent(value)}`);
        setBusinesses(response.data.data || []);
      } catch (error: any) {
        message.error('Failed to search businesses');
      } finally {
        setBusinessesLoading(false);
      }
    }
  };

  const handleView = async (report: CustomerReport) => {
    try {
      const response = await api.get(`/reports/${report.id}/view`, {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], { type: response.headers['content-type'] || report.mime_type });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error: any) {
      message.error('Failed to view report');
    }
  };

  const handleDownload = async (report: CustomerReport) => {
    try {
      const response = await api.get(`/reports/${report.id}/download`, {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], { type: response.headers['content-type'] || report.mime_type });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = report.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error: any) {
      message.error('Failed to download report');
    }
  };

  const handleDelete = async (report: CustomerReport) => {
    try {
      await api.delete(`/reports/${report.id}`);
      message.success('Report deleted successfully');
      if (selectedFolder) {
        const businessId = entityType === 'business' && selectedBusiness ? selectedBusiness.id : null;
        loadReports(selectedFolder.customer_id, selectedFolder.folder, businessId);
      } else if (entityType === 'customer' && selectedCustomer) {
        loadFolders(selectedCustomer.id, null);
      } else if (entityType === 'business' && selectedBusiness) {
        loadFolders(selectedBusiness.customer_id, selectedBusiness.id);
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to delete report');
    }
  };

  const handleCreateFolder = async (values: any) => {
    try {
      if (entityType === 'customer' && !selectedCustomer) {
        message.error('Please select a customer first');
        return;
      }
      if (entityType === 'business' && !selectedBusiness) {
        message.error('Please select a business first');
        return;
      }
      
      const customerId = entityType === 'customer' ? selectedCustomer!.id : await resolveBusinessCustomerId();
      if (!customerId) {
        message.error('Please select a valid business customer');
        return;
      }
      const businessId = entityType === 'business' ? selectedBusiness!.id : null;
      
      await api.post('/reports/folders', {
        customer_id: customerId,
        business_id: businessId,
        folder: values.folder,
        report_type: values.report_type,
      });

      message.success('Folder created successfully');
      setCreateFolderModalVisible(false);
      createFolderForm.resetFields();
      if (entityType === 'customer' && selectedCustomer) {
        loadFolders(selectedCustomer.id, null);
      } else if (entityType === 'business' && selectedBusiness) {
        loadFolders(selectedBusiness.customer_id, selectedBusiness.id);
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to create folder');
    }
  };

  const handleDeleteFolder = async (folder: Folder) => {
    try {
      if (entityType === 'customer' && !selectedCustomer) {
        message.error('Please select a customer first');
        return;
      }
      if (entityType === 'business' && !selectedBusiness) {
        message.error('Please select a business first');
        return;
      }
      
      const businessId = entityType === 'business' && selectedBusiness ? selectedBusiness.id : null;
      
      await api.delete('/reports/folders', {
        data: {
          customer_id: folder.customer_id,
          business_id: businessId,
          folder: folder.folder,
        },
      });

      message.success('Folder deleted successfully');
      if (entityType === 'customer' && selectedCustomer) {
        loadFolders(selectedCustomer.id, null);
      } else if (entityType === 'business' && selectedBusiness) {
        loadFolders(selectedBusiness.customer_id, selectedBusiness.id);
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to delete folder');
    }
  };

  const handleEditFolder = async (values: any) => {
    if (!editingFolder) return;
    try {
      await api.put('/reports/folders', {
        customer_id: editingFolder.customer_id,
        business_id: editingFolder.business_id ?? null,
        folder: editingFolder.folder,
        new_folder: values.folder,
        report_type: values.report_type,
      });

      message.success('Folder updated successfully');
      setEditFolderModalVisible(false);
      setEditingFolder(null);
      editFolderForm.resetFields();
      if (entityType === 'customer' && selectedCustomer) {
        loadFolders(selectedCustomer.id, null);
      } else if (entityType === 'business' && selectedBusiness) {
        loadFolders(selectedBusiness.customer_id, selectedBusiness.id);
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update folder');
    }
  };

  // 批量上传单个文件
  const uploadSingleFile = async (file: File, fileId: string) => {
    try {
      if (entityType === 'customer' && !selectedCustomer) {
        throw new Error('Please select a customer first');
      }
      if (entityType === 'business' && !selectedBusiness) {
        throw new Error('Please select a business first');
      }
      if (!selectedFolder) {
        throw new Error('Please select a folder first');
      }

      setUploadStatus(prev => {
        const newStatus: Record<string, 'pending' | 'uploading' | 'success' | 'error'> = { ...prev, [fileId]: 'uploading' };
        uploadStatusRef.current = newStatus;
        return newStatus;
      });

      let businessId: number | null = null;
      if (selectedFolder?.business_id !== undefined && selectedFolder.business_id !== null) {
        businessId = selectedFolder.business_id;
      } else if (entityType === 'business' && selectedBusiness) {
        businessId = selectedBusiness.id;
      }

      const customerId = entityType === 'customer' ? selectedCustomer!.id : await resolveBusinessCustomerId();
      if (!customerId) {
        throw new Error('Please select a valid business customer');
      }

      const formData = new FormData();
      formData.append('customer_id', customerId.toString());
      if (businessId) {
        formData.append('business_id', businessId.toString());
      }
      formData.append('file', file);
      formData.append('folder', selectedFolder.folder);
      // Use folder year if available, otherwise use current year
      const year = selectedFolder.year || new Date().getFullYear();
      formData.append('year', year.toString());

      await api.post('/reports', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({ ...prev, [fileId]: percent }));
          }
        },
      });

      setUploadStatus(prev => {
        const newStatus: Record<string, 'pending' | 'uploading' | 'success' | 'error'> = { ...prev, [fileId]: 'success' };
        uploadStatusRef.current = newStatus;
        return newStatus;
      });
      setUploadProgress(prev => ({ ...prev, [fileId]: 100 }));
    } catch (error: any) {
      setUploadStatus(prev => {
        const newStatus: Record<string, 'pending' | 'uploading' | 'success' | 'error'> = { ...prev, [fileId]: 'error' };
        uploadStatusRef.current = newStatus;
        return newStatus;
      });
      console.error(`Failed to upload ${file.name}:`, error);
    }
  };

  // 处理上传队列
  const processUploadQueue = useCallback(async () => {
    if (isProcessingRef.current) {
      return;
    }

    const checkAndProcess = async () => {
      if (uploadQueueRef.current.length === 0) {
        isProcessingRef.current = false;
        return;
      }

      // 获取待上传的文件（只处理 pending 状态的）
      const pendingUploads = uploadQueueRef.current
        .filter(item => {
          const status = uploadStatusRef.current[item.id] || uploadStatus[item.id];
          return status === 'pending';
        })
        .slice(0, CONCURRENT_UPLOADS);

      if (pendingUploads.length === 0) {
        // 检查是否所有文件都已完成
        const allCompleted = uploadQueueRef.current.every(item => {
          const status = uploadStatusRef.current[item.id] || uploadStatus[item.id];
          return status === 'success' || status === 'error';
        });

        if (allCompleted && uploadQueueRef.current.length > 0) {
          // 所有文件上传完成，刷新列表
          if (selectedFolder) {
            const businessId = selectedFolder.business_id ?? 
              (entityType === 'business' && selectedBusiness ? selectedBusiness.id : null);
            await loadReports(selectedFolder.customer_id, selectedFolder.folder, businessId);
          }
          
          // 清理已完成的上传
          const currentStatus = uploadStatusRef.current;
          const successCount = Object.values(currentStatus).filter(s => s === 'success').length;
          const errorCount = Object.values(currentStatus).filter(s => s === 'error').length;
          
          if (successCount > 0) {
            message.success(`${successCount} file(s) uploaded successfully`);
          }
          if (errorCount > 0) {
            message.error(`${errorCount} file(s) failed to upload`);
          }

          setUploadQueue([]);
          uploadQueueRef.current = [];
          setUploadProgress({});
          setUploadStatus({});
          uploadStatusRef.current = {};
          isProcessingRef.current = false;
          return;
        }

        // 还有正在上传的文件，等待一下再检查
        setTimeout(() => {
          if (!isProcessingRef.current) {
            checkAndProcess();
          }
        }, 500);
        return;
      }

      // 启动新的上传
      const uploadPromises = pendingUploads.map(item => uploadSingleFile(item.file, item.id));
      await Promise.allSettled(uploadPromises);

      // 继续处理剩余的文件
      setTimeout(() => {
        checkAndProcess();
      }, 100);
    };

    isProcessingRef.current = true;
    checkAndProcess();
  }, [selectedFolder, selectedCustomer, selectedBusiness, entityType]);

  // 当上传队列变化时处理队列
  useEffect(() => {
    if (uploadQueue.length > 0 && !isProcessingRef.current) {
      processUploadQueue();
    }
  }, [uploadQueue.length]); // 只依赖队列长度，避免重复触发

  // 处理拖拽文件
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedFolder && !isCustomer) {
      setIsDragging(true);
    }
  }, [selectedFolder, isCustomer]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (!selectedFolder) {
      message.warning('Please select a folder first');
      return;
    }

    if (isCustomer) {
      message.warning('Customers cannot upload reports');
      return;
    }

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length === 0) return;

    const newFiles = droppedFiles.map(file => ({
      file,
      id: `${Date.now()}-${Math.random()}-${file.name}`,
    }));

    setUploadQueue(prev => [...prev, ...newFiles]);
    uploadQueueRef.current = [...uploadQueueRef.current, ...newFiles];
    
    newFiles.forEach(item => {
      setUploadStatus(prev => {
        const newStatus: Record<string, 'pending' | 'uploading' | 'success' | 'error'> = { ...prev, [item.id]: 'pending' };
        uploadStatusRef.current = newStatus;
        return newStatus;
      });
      setUploadProgress(prev => ({ ...prev, [item.id]: 0 }));
    });

    message.info(`Added ${droppedFiles.length} file(s) to upload queue`);
  }, [selectedFolder, isCustomer]);

  const handleUpload = async (values: any) => {
    try {
      if (entityType === 'customer' && !selectedCustomer) {
        message.error('Please select a customer first');
        return;
      }
      if (entityType === 'business' && !selectedBusiness) {
        message.error('Please select a business first');
        return;
      }
      
      setUploading(true);
      
      // Use business_id from selectedFolder if available, otherwise determine from entityType
      let businessId: number | null = null;
      if (selectedFolder?.business_id !== undefined && selectedFolder.business_id !== null) {
        businessId = selectedFolder.business_id;
      } else if (entityType === 'business' && selectedBusiness) {
        businessId = selectedBusiness.id;
      }
      
      const customerId = entityType === 'customer' ? selectedCustomer!.id : await resolveBusinessCustomerId();
      if (!customerId) {
        message.error('Please select a valid business customer');
        return;
      }
      
      const formData = new FormData();
      formData.append('customer_id', customerId.toString());
      if (businessId) {
        formData.append('business_id', businessId.toString());
      }
      formData.append('file', values.file.file);
      const folderName = values.folder || selectedFolder?.folder || 'reports';
      formData.append('folder', folderName);
      formData.append('year', values.year.toString());
      if (values.description) {
        formData.append('description', values.description);
      }

      await api.post('/reports', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      message.success('Report uploaded successfully');
      setUploadModalVisible(false);
      uploadForm.resetFields();
      
      // Refresh the report list
      if (selectedFolder) {
        // Use business_id from selectedFolder if available
        const folderBusinessId = selectedFolder.business_id ?? businessId;
        await loadReports(selectedFolder.customer_id, selectedFolder.folder, folderBusinessId);
      } else if (entityType === 'customer' && selectedCustomer) {
        await loadFolders(selectedCustomer.id, null);
      } else if (entityType === 'business' && selectedBusiness) {
        await loadFolders(selectedBusiness.customer_id, selectedBusiness.id);
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to upload report');
    } finally {
      setUploading(false);
    }
  };

  const getFileIcon = (type: string) => {
    const iconStyle = { fontSize: 48, color: '#ff4d4f' };
    return <FileTextOutlined style={iconStyle} />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes >= 1073741824) {
      return (bytes / 1073741824).toFixed(2) + ' GB';
    } else if (bytes >= 1048576) {
      return (bytes / 1048576).toFixed(2) + ' MB';
    } else if (bytes >= 1024) {
      return (bytes / 1024).toFixed(2) + ' KB';
    } else {
      return bytes + ' bytes';
    }
  };

  const formatUploadDate = (value?: string | null): string => {
    if (!value) return '-';
    const dateValue = dayjs(value);
    if (!dateValue.isValid()) return '-';
    return dateValue.format('DD/MM/YYYY');
  };

  const getCustomerInitials = (name: string): string => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const filteredCustomers = customers.filter((customer) =>
    customer.name.toLowerCase().includes(searchText.toLowerCase())
  );
  const filteredBusinesses = businesses.filter((business) =>
    business.name.toLowerCase().includes(searchText.toLowerCase())
  );
  const filteredFolders = folders.filter((folder) =>
    folder.folder.toLowerCase().includes(folderSearchText.toLowerCase())
  );
  const filteredReports = reports.filter((report) =>
    report.name.toLowerCase().includes(reportSearchText.toLowerCase())
  );

  const folderColumns = [
    {
      title: 'Name',
      dataIndex: 'folder',
      key: 'folder',
      render: (_: string, folder: Folder) => (
        <span style={{ color: '#1890ff', cursor: 'pointer' }}>{folder.folder}</span>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'report_type',
      key: 'report_type',
      render: (value: Folder['report_type']) => value || '-',
    },
    {
      title: 'Files',
      dataIndex: 'file_count',
      key: 'file_count',
      render: (value: number | undefined) => value ?? 0,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, folder: Folder) => (
        <Space>
          {!isCustomer && canUpdate && (
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={(event) => {
                event.stopPropagation();
                setEditingFolder(folder);
                editFolderForm.setFieldsValue({
                  folder: folder.folder,
                  report_type: folder.report_type || undefined,
                });
                setEditFolderModalVisible(true);
              }}
            />
          )}
          {!isCustomer && (
            <Popconfirm
              title="Are you sure you want to delete this folder?"
              description="This will delete all reports in this folder. This action cannot be undone."
              onConfirm={(event) => {
                event?.stopPropagation();
                handleDeleteFolder(folder);
              }}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ danger: true }}
            >
              <Button
                danger
                type="text"
                icon={<DeleteOutlined />}
                onClick={(event) => event.stopPropagation()}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const reportColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (value: string) => value?.toUpperCase() || '-',
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      render: (value: number) => formatFileSize(value),
    },
    {
      title: 'Uploaded',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (value: string) => formatUploadDate(value),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, report: CustomerReport) => (
        <Space>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => handleView(report)}
            size="small"
          >
            View
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(report)}
            size="small"
          />
          {!isCustomer && (
            <Popconfirm
              title="Are you sure you want to delete this report?"
              onConfirm={() => handleDelete(report)}
              okText="Yes"
              cancelText="No"
            >
              <Button danger icon={<DeleteOutlined />} size="small" />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Layout style={{ height: 'calc(100vh - 112px)', background: '#fff', overflow: 'hidden', display: 'flex', margin: '-24px' }}>
      <Sider
        width={300}
        style={{
          background: '#fff',
          borderRight: '1px solid #f0f0f0',
          paddingRight: '16px',
          paddingTop: 0,
          paddingLeft: 0,
          paddingBottom: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <div style={{ padding: '16px 16px 0 16px', flexShrink: 0 }}>
        <Title level={3} style={{ marginTop: 0, marginBottom: 16 }}>
          Reports
        </Title>
        <Radio.Group
          value={entityType}
          onChange={(e) => setEntityType(e.target.value)}
          style={{ marginBottom: 16, width: '100%' }}
        >
          <Radio.Button value="customer" style={{ width: '50%', textAlign: 'center' }}>
            Personal
          </Radio.Button>
          <Radio.Button value="business" style={{ width: '50%', textAlign: 'center' }}>
            Business
          </Radio.Button>
        </Radio.Group>
        <Input
          placeholder={`Search ${entityType === 'customer' ? 'Personal' : 'Business'}`}
          prefix={<SearchOutlined />}
          style={{ marginBottom: 16 }}
          onChange={(e) => handleSearch(e.target.value)}
          allowClear
        />
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px 16px', minHeight: 0, maxHeight: '70%' }}>
        {entityType === 'customer' ? (
          <List
            loading={customersLoading}
            dataSource={filteredCustomers}
              pagination={false}
              style={{ minHeight: 0 }}
            renderItem={(customer) => (
              <List.Item
                style={{
                  cursor: 'pointer',
                  padding: '12px',
                  borderRadius: 4,
                  backgroundColor:
                    selectedCustomer?.id === customer.id ? '#e6f7ff' : 'transparent',
                  marginBottom: 8,
                }}
                onClick={() => setSelectedCustomer(customer)}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      style={{
                        backgroundColor: '#1890ff',
                        color: '#fff',
                      }}
                    >
                      {getCustomerInitials(customer.name)}
                    </Avatar>
                  }
                  title={customer.name}
                />
              </List.Item>
            )}
          />
        ) : (
          <List
            loading={businessesLoading}
            dataSource={businesses.filter((business) =>
              business.name.toLowerCase().includes(searchText.toLowerCase())
            )}
              pagination={false}
              style={{ minHeight: 0 }}
            renderItem={(business) => (
              <List.Item
                style={{
                  cursor: 'pointer',
                  padding: '12px',
                  borderRadius: 4,
                  backgroundColor:
                    selectedBusiness?.id === business.id ? '#e6f7ff' : 'transparent',
                  marginBottom: 8,
                }}
                onClick={() => setSelectedBusiness(business)}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      style={{
                        backgroundColor: '#52c41a',
                        color: '#fff',
                      }}
                    >
                      {getCustomerInitials(business.name)}
                    </Avatar>
                  }
                  title={business.name}
                />
              </List.Item>
            )}
          />
        )}
        </div>
      </Sider>
      <Content 
        style={{ 
          flex: 1,
          overflow: 'hidden',
          background: '#fff',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          border: isDragging && selectedFolder && !isCustomer ? '2px dashed #1890ff' : '2px dashed transparent',
          borderRadius: isDragging && selectedFolder && !isCustomer ? 8 : 0,
          backgroundColor: isDragging && selectedFolder && !isCustomer ? '#e6f7ff' : '#fff',
          transition: 'all 0.3s',
        }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
        {isDragging && selectedFolder && !isCustomer && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              zIndex: 10,
              pointerEvents: 'none',
            }}
          >
            <UploadOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
            <div style={{ fontSize: 18, color: '#1890ff', fontWeight: 500 }}>
              Drop files here to upload
            </div>
          </div>
        )}
        {selectedFolder ? (
          // Third level: Reports view
          <>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={3} style={{ margin: 0 }}>
                  {selectedFolder.folder}{' '}
                  (
                  <span
                    style={{ color: '#1890ff', cursor: 'pointer' }}
                    onClick={() => {
                      setSelectedFolder(null);
                      setReports([]);
                    }}
                  >
                    {selectedFolder.customer_name}
                  </span>
                  )
                </Title>
              <Space>
                <Input
                  placeholder="Search files"
                  prefix={<SearchOutlined />}
                  value={reportSearchText}
                  onChange={(e) => setReportSearchText(e.target.value)}
                  allowClear
                  style={{ width: 220 }}
                />
                <Radio.Group
                  value={reportView}
                  onChange={(e) => setReportView(e.target.value)}
                >
                  <Radio.Button value="grid">
                    <AppstoreOutlined />
                  </Radio.Button>
                  <Radio.Button value="list">
                    <BarsOutlined />
                  </Radio.Button>
                </Radio.Group>
              {!isCustomer && canCreate && (
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={() => setUploadModalVisible(true)}
                >
                  Upload Report
                </Button>
              )}
              </Space>
            </div>
            {/* Upload Progress */}
            {uploadQueue.length > 0 && (
              <Card style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong>Uploading {uploadQueue.length} file(s)</Text>
                </div>
                <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                  {uploadQueue.map((item) => {
                    const status = uploadStatus[item.id] || 'pending';
                    const progress = uploadProgress[item.id] || 0;
                    return (
                      <div key={item.id} style={{ marginBottom: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <Text style={{ fontSize: '12px' }} ellipsis>
                            {item.file.name}
                          </Text>
                          <Tag
                            color={
                              status === 'success' ? 'green' :
                              status === 'error' ? 'red' :
                              status === 'uploading' ? 'blue' : 'default'
                            }
                          >
                            {status === 'success' ? 'Done' :
                             status === 'error' ? 'Failed' :
                             status === 'uploading' ? 'Uploading' : 'Pending'}
                          </Tag>
                        </div>
                        {status === 'uploading' && (
                          <Progress percent={progress} size="small" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}
            {reportView === 'grid' ? (
              <Row gutter={[16, 16]}>
                {filteredReports.map((report) => (
                  <Col key={report.id} xs={24} sm={12} md={12} lg={12}>
                    <Card
                      style={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                      bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '16px' }}
                    >
                      <div style={{ display: 'flex', gap: 16, flex: 1 }}>
                        <div style={{ textAlign: 'center', minWidth: 60 }}>
                          {getFileIcon(report.type)}
                        </div>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                          <div>
                            <Text strong style={{ display: 'block', marginBottom: 4, fontSize: '14px' }}>
                              {report.name}
                            </Text>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {report.type.toUpperCase()} • {formatFileSize(report.size)}
                            </Text>
                            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
                              Uploaded: {formatUploadDate(report.created_at)}
                            </Text>
                          </div>
                          <Space style={{ marginTop: 12 }}>
                            <Button
                              type="primary"
                              icon={<EyeOutlined />}
                              onClick={() => handleView(report)}
                              size="small"
                            >
                              View
                            </Button>
                            <Button
                              icon={<DownloadOutlined />}
                              onClick={() => handleDownload(report)}
                              size="small"
                            />
                            {!isCustomer && (
                              <Popconfirm
                                title="Are you sure you want to delete this report?"
                                onConfirm={() => handleDelete(report)}
                                okText="Yes"
                                cancelText="No"
                              >
                                <Button
                                  danger
                                  icon={<DeleteOutlined />}
                                  size="small"
                                />
                              </Popconfirm>
                            )}
                          </Space>
                        </div>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <Table
                dataSource={filteredReports}
                columns={reportColumns}
                rowKey="id"
                pagination={false}
                size="small"
              />
            )}
            {filteredReports.length === 0 && !loading && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                No reports found
              </div>
            )}
          </>
        ) : (entityType === 'customer' && selectedCustomer) || (entityType === 'business' && selectedBusiness) ? (
          // Second level: Folders view (cards)
          <>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Title level={3} style={{ margin: 0 }}>
                {entityType === 'customer' ? selectedCustomer?.name : selectedBusiness?.name}
              </Title>
              <Space>
                <Input
                  placeholder="Search folders"
                  prefix={<SearchOutlined />}
                  value={folderSearchText}
                  onChange={(e) => setFolderSearchText(e.target.value)}
                  allowClear
                  style={{ width: 220 }}
                />
                <Radio.Group
                  value={folderView}
                  onChange={(e) => setFolderView(e.target.value)}
                >
                  <Radio.Button value="grid">
                    <AppstoreOutlined />
                  </Radio.Button>
                  <Radio.Button value="list">
                    <BarsOutlined />
                  </Radio.Button>
                </Radio.Group>
                {!isCustomer && canCreate && (
                  <Button
                    type="primary"
                    icon={<FolderOutlined />}
                    onClick={() => setCreateFolderModalVisible(true)}
                  >
                    Create Folder
                  </Button>
                )}
              </Space>
            </div>
            {folderView === 'grid' ? (
              <Row gutter={[16, 16]}>
                {filteredFolders.map((folder, index) => {
                  const colors = ['#1890ff', '#722ed1', '#eb2f96', '#fa8c16', '#52c41a'];
                  const color = colors[index % colors.length];
                  
                  return (
                    <Col key={`${folder.customer_id}-${folder.folder}`} xs={24} sm={12} md={8} lg={8}>
                      <Card
                        hoverable
                        onClick={() => setSelectedFolder(folder)}
                        style={{
                          borderRadius: 8,
                          textAlign: 'center',
                          cursor: 'pointer',
                          position: 'relative',
                        }}
                        bodyStyle={{ padding: '24px' }}
                      >
                        {!isCustomer && (
                          <div
                            style={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              zIndex: 10,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            {!isCustomer && canUpdate && (
                              <Button
                                type="text"
                                icon={<EditOutlined />}
                                size="small"
                                onClick={() => {
                                  setEditingFolder(folder);
                                  editFolderForm.setFieldsValue({
                                    folder: folder.folder,
                                    report_type: folder.report_type || undefined,
                                  });
                                  setEditFolderModalVisible(true);
                                }}
                              />
                            )}
                            <Popconfirm
                              title="Are you sure you want to delete this folder?"
                              description="This will delete all reports in this folder. This action cannot be undone."
                              onConfirm={() => handleDeleteFolder(folder)}
                              okText="Yes"
                              cancelText="No"
                              okButtonProps={{ danger: true }}
                            >
                              <Button
                                danger
                                type="text"
                                icon={<DeleteOutlined />}
                                size="small"
                                style={{
                                  color: '#ff4d4f',
                                }}
                              />
                            </Popconfirm>
                          </div>
                        )}
                        <div style={{ marginBottom: 8 }}>
                          <FolderOutlined
                            style={{
                              fontSize: 64,
                              background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              color: color,
                            }}
                          />
                        </div>
                        <Title level={5} style={{ marginBottom: 8, marginTop: 0 }}>
                          {folder.folder}
                        </Title>
                        <div style={{ marginBottom: 8, display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
                          {folder.report_type && (
                            <Tag color={folder.report_type === 'SSM' ? 'blue' : 'green'}>
                              Type: {folder.report_type}
                            </Tag>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                          <FileTextOutlined />
                          <Text type="secondary">
                            {folder.file_count || 0} {folder.file_count === 1 ? 'file' : 'files'}
                          </Text>
                        </div>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            ) : (
              <Table
                dataSource={filteredFolders}
                columns={folderColumns}
                rowKey={(folder) => `${folder.customer_id}-${folder.business_id ?? 'null'}-${folder.folder}`}
                pagination={false}
                size="small"
                onRow={(folder) => ({
                  onClick: () => setSelectedFolder(folder),
                })}
              />
            )}
            {folders.length === 0 && !foldersLoading && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                No folders found for this customer
              </div>
            )}
          </>
        ) : (
          // First level: No selection
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            {customers.length === 0 ? (
              <div>No customers found</div>
            ) : (
              <div>Select a customer to view folders</div>
            )}
          </div>
        )}
        </div>

        <Modal
          title="Create Folder"
          open={createFolderModalVisible}
          onCancel={() => {
            setCreateFolderModalVisible(false);
            createFolderForm.resetFields();
          }}
          footer={null}
        >
          <Form
            form={createFolderForm}
            layout="vertical"
            onFinish={handleCreateFolder}
          >
            <Form.Item
              name="folder"
              label="Folder Name"
              rules={[{ required: true, message: 'Please enter folder name' }]}
            >
              <Input placeholder="Enter folder name" />
            </Form.Item>
            <Form.Item
              name="report_type"
              label="Type"
              rules={[{ required: true, message: 'Please select report type' }]}
            >
              <Select placeholder="Select type">
                <Option value="SSM">SSM</Option>
                <Option value="Financial">Financial</Option>
              </Select>
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  Create
                </Button>
                <Button onClick={() => {
                  setCreateFolderModalVisible(false);
                  createFolderForm.resetFields();
                }}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="Edit Folder"
          open={editFolderModalVisible}
          onCancel={() => {
            setEditFolderModalVisible(false);
            setEditingFolder(null);
            editFolderForm.resetFields();
          }}
          footer={null}
        >
          <Form
            form={editFolderForm}
            layout="vertical"
            onFinish={handleEditFolder}
          >
            <Form.Item
              name="folder"
              label="Folder Name"
              rules={[{ required: true, message: 'Please enter folder name' }]}
            >
              <Input placeholder="Enter folder name" />
            </Form.Item>
            <Form.Item
              name="report_type"
              label="Type"
              rules={[{ required: true, message: 'Please select report type' }]}
            >
              <Select placeholder="Select type">
                <Option value="SSM">SSM</Option>
                <Option value="Financial">Financial</Option>
              </Select>
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  Save
                </Button>
                <Button
                  onClick={() => {
                    setEditFolderModalVisible(false);
                    setEditingFolder(null);
                    editFolderForm.resetFields();
                  }}
                >
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="Upload Report"
          open={uploadModalVisible}
          onCancel={() => {
            if (!uploading) {
              setUploadModalVisible(false);
              uploadForm.resetFields();
              setUploading(false);
            }
          }}
          closable={!uploading}
          maskClosable={!uploading}
          footer={null}
        >
          <Form
            form={uploadForm}
            layout="vertical"
            onFinish={handleUpload}
          >
            <Form.Item
              name="file"
              label="File"
              rules={[{ required: true, message: 'Please select a file' }]}
            >
              <Upload
                beforeUpload={() => false}
                maxCount={1}
              >
                <Button icon={<UploadOutlined />}>Select File</Button>
              </Upload>
            </Form.Item>
            <Form.Item
              name="folder"
              label="Folder"
              initialValue={selectedFolder?.folder || ''}
            >
              <Input placeholder="Folder name" disabled={!!selectedFolder} />
            </Form.Item>
            <Form.Item
              name="year"
              label="Year"
              rules={[{ required: true, message: 'Please enter year' }]}
            >
              <InputNumber
                placeholder="Enter year"
                min={2000}
                max={2100}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item
              name="description"
              label="Description (Optional)"
            >
              <TextArea rows={3} placeholder="Report description" />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<UploadOutlined />}
                  loading={uploading}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
                <Button
                  onClick={() => {
                    setUploadModalVisible(false);
                    uploadForm.resetFields();
                  }}
                  disabled={uploading}
                >
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </Content>
    </Layout>
  );
};
